/**
 * STITCH-2 SCOPE 11 (F11) -- the ONE global clinic-context provider.
 *
 * Enterprise-CRM-style location selector state for the whole app shell:
 * single-clinic staff see a fixed clinic name, multi-clinic roles pick among
 * their ASSIGNED clinics, the owner gets "All clinics" plus each clinic.
 * The selection is UI NARROWING WITHIN SERVER-VERIFIED SCOPE, never an
 * authorization concept (GLOBAL-CLINIC-CONTEXT-01-SCOPING hard rule 1):
 * options come from the PHI-safe /me clinicOptions signal (id + name only,
 * the leads-workspace picker projection), every request still passes the
 * backend gates, and a UI bug can only ever show LESS, never more.
 *
 * PERSISTENCE (the repo's session idiom, cited): per-tab sessionStorage --
 * the same storage class the walk-in session already uses
 * (frontend/src/walkin/session.ts idiom). The stored value is a clinic id
 * ONLY (never PHI) and is validated against the CURRENT verified options on
 * load: an id that is no longer in scope falls back to All/fixed
 * (fail-closed narrowing).
 *
 * ADOPTED (Stage A): Leads workspace default clinic, Billing entry default
 * clinic, Patients list (SCOPE 10), the walk-in Today plan default.
 * DEFERRED (Stage B, per the scoping inventory): owner Overview /
 * Clinics / Growth aggregate narrowing (their endpoints carry per-clinic
 * rows; narrowing lands per the V5 endpoint inventory).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { DashboardMe } from '@/dashboard/types';
import { useDashboardMeContext } from '@/dashboard/me-context';

export const CLINIC_CONTEXT_STORAGE_KEY = 'adoracare.clinic-context.v1';

/** '' = All clinics (the caller's whole verified scope). */
export const ALL_CLINICS = '';

export interface ClinicOption {
  readonly clinicId: number;
  readonly clinicLabel: string | null;
}

export interface ClinicContextValue {
  /** Verified options from /me (empty until /me resolves). */
  readonly options: readonly ClinicOption[];
  /** The selected clinic id, or null = All clinics / whole scope. */
  readonly selectedClinicId: number | null;
  /** Human label for the current selection (never a raw id). */
  readonly selectedLabel: string;
  /** Selector mode the header should render. */
  readonly mode: 'none' | 'fixed' | 'picker';
  readonly select: (clinicId: number | null) => void;
}

const ClinicContext = createContext<ClinicContextValue | null>(null);

/** A clinic with no name on file. A WORD, never a number: the founder ruling
 *  takes raw clinic ids off the glass everywhere, and an id is not a name --
 *  it tells an operator nothing they can act on. An unnamed clinic is a tenant
 *  data gap, and this states it honestly instead of printing a database key. */
export const UNNAMED_CLINIC_LABEL = 'Unnamed clinic';

/** THE ONE clinic labeller. Every surface that names a clinic goes through
 *  here, so there is exactly one answer to "what do we call this clinic". */
export function clinicOptionLabel(option: ClinicOption): string {
  return option.clinicLabel !== null && option.clinicLabel.trim() !== ''
    ? option.clinicLabel
    : UNNAMED_CLINIC_LABEL;
}

/** The same rule for the many places holding a loose label + id pair off the
 *  wire (search results, station rows, recovery rows) rather than a
 *  ClinicOption. Keeps those sites from inventing their own fallback. */
export function clinicLabelOf(
  clinicLabel: string | null | undefined,
  _clinicId?: number | null,
): string {
  return clinicLabel !== null && clinicLabel !== undefined && clinicLabel.trim() !== ''
    ? clinicLabel
    : UNNAMED_CLINIC_LABEL;
}

function readStoredSelection(): number | null {
  try {
    const raw = sessionStorage.getItem(CLINIC_CONTEXT_STORAGE_KEY);
    if (raw === null || raw === ALL_CLINICS) return null;
    const id = Number(raw);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function storeSelection(clinicId: number | null): void {
  try {
    if (clinicId === null) {
      sessionStorage.setItem(CLINIC_CONTEXT_STORAGE_KEY, ALL_CLINICS);
    } else {
      sessionStorage.setItem(CLINIC_CONTEXT_STORAGE_KEY, String(clinicId));
    }
  } catch {
    // Storage unavailable (privacy mode) -> selection lives for the render only.
  }
}

/** Pure derivation of the context value (exported for tests). */
export function deriveClinicContext(args: {
  me: DashboardMe | null;
  storedSelection: number | null;
  select: (clinicId: number | null) => void;
}): ClinicContextValue {
  const { me, storedSelection, select } = args;
  const options: readonly ClinicOption[] = me?.clinicOptions ?? [];
  const isOwner = me?.role === 'owner';

  // Fail-closed validation: a stored id outside the CURRENT verified
  // options falls back to All (owner/multi) or the single clinic (fixed).
  const validSelection =
    storedSelection !== null &&
    options.some((o) => o.clinicId === storedSelection)
      ? storedSelection
      : null;

  if (options.length === 0) {
    return {
      options,
      selectedClinicId: null,
      selectedLabel: isOwner ? 'All clinics' : '',
      mode: 'none',
      select,
    };
  }

  // ONE option = ONE answer, for EVERY role including the owner. There is
  // nothing to pick, so the lens resolves it rather than leaving the selection
  // null and every downstream surface asking "which clinic?" forever.
  // 119-CLINIC-OPERATING-SPINE-V1: the owner was previously excluded here, so a
  // SINGLE-CLINIC practice owner sat on "All clinics" with no id to send and
  // read an empty day board. Resolving it in THE LENS fixes every surface at
  // once -- the lens owns "where am I", and no surface re-derives it.
  if (options.length === 1) {
    const only = options[0]!;
    return {
      options,
      selectedClinicId: only.clinicId,
      selectedLabel: clinicOptionLabel(only),
      mode: 'fixed',
      select,
    };
  }

  const selected =
    validSelection !== null
      ? (options.find((o) => o.clinicId === validSelection) ?? null)
      : null;
  return {
    options,
    selectedClinicId: selected?.clinicId ?? null,
    selectedLabel:
      selected !== null
        ? clinicOptionLabel(selected)
        : isOwner
          ? 'All clinics'
          : 'All assigned clinics',
    mode: 'picker',
    select,
  };
}

export function ClinicContextProvider({
  me,
  children,
}: {
  me: DashboardMe | null;
  children: ReactNode;
}): JSX.Element {
  const [stored, setStored] = useState<number | null>(() => readStoredSelection());

  const select = useCallback((clinicId: number | null) => {
    storeSelection(clinicId);
    setStored(clinicId);
  }, []);

  const value = useMemo(
    () => deriveClinicContext({ me, storedSelection: stored, select }),
    [me, stored, select],
  );

  return <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>;
}

/** Null outside the provider (pages treat that as "no global context"). */
export function useClinicContext(): ClinicContextValue | null {
  return useContext(ClinicContext);
}

/**
 * THE LENS, RESILIENT TO WHICHEVER SHELL IS MOUNTED.
 *
 * There are two independent `/me` fetches in this app: `DashboardLayout`
 * fetches one and provides it through DashboardMeContext, while `AppLayout`
 * fetches its own ONLY for a signed-in session and feeds it to the provider
 * above. So a shell can legitimately have a resolved `me` while the lens has
 * none, and a surface that reads the lens alone would then render nothing --
 * which is exactly what happened to the money desk when it stopped reading
 * `me` directly and started reading the lens.
 *
 * This hook is the ONE place that reconciles them, so surfaces still never
 * re-derive scope: the real provider wins whenever it carries options, and the
 * dashboard's own `me` backs it up when it does not. The fallback is
 * READ-ONLY -- `select` is a no-op, because a selection with nowhere to persist
 * would silently lose the operator's choice; a shell that can select always has
 * the real provider.
 */
export function useClinicLens(): ClinicContextValue | null {
  const provided = useContext(ClinicContext);
  const dashboardMe = useDashboardMeContext();
  const fallback = useMemo(
    () =>
      deriveClinicContext({
        me: dashboardMe?.me ?? null,
        storedSelection: readStoredSelection(),
        select: () => undefined,
      }),
    [dashboardMe?.me],
  );
  if (provided !== null && provided.options.length > 0) return provided;
  return fallback.options.length > 0 ? fallback : provided;
}
